import { useMemo, useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { useForm } from 'react-hook-form';
import { Button } from '@components/button';
import { quoteStatusTerminal } from '@models/quotes';
import { Box, Grid } from '@mui/material';
import { SidebarHeader } from '@pages/QuoteDetails/sidebarItems/SidebarHeader';
import { SubjectivityItem } from '@pages/QuoteDetails/sidebarItems/subjectivities/SubjectivitiesItem';
import { SubjectivitiesTab } from '@pages/QuoteDetails/sidebarItems/subjectivities/SubjectivitiesPanel';
import { useQuote } from '@services/quotes';
import { useCreateSubjectivity } from '@services/subjectivity/useCreateSubjectivity';
import { useUpdateSubjectivity } from '@services/subjectivity/useUpdateSubjectivity';
import { useStore } from '@store/GlobalStore';
import { Form, FormSection } from '@components/forms';
import { TFormField } from '@models/forms';
import { SubjectivityLibraryTypeAhead } from '@pages/QuoteDetails/sidebarItems/subjectivities/SubjectivityLibraryTypeAhead';
import {
  TSubjectivityLibraryItem,
  useSearchPaginatedSubjectivityLibrary,
} from '@services/subjectivity/subjectivity-library/useSearchPaginatedSubjectivityLibrary';
import { SubjectivityBulkAddForm } from '@components/Subjectivities/quote-subjectivities/SubjectivityBulkAddForm';
import { useCreateSubjectivityLibraryItem } from '@services/subjectivity/subjectivity-library/useCreateSubjectivityLibraryItem';
import { enqueueSnackbar } from 'notistack';
import { ToastType } from '@components/toast';
import { insuranceLineIds } from '@models/insuranceLine';
import { getSubjectivitiesFormFields } from '@components/Subjectivities/quote-subjectivities/getSubjectivitiesFormFields';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import invariant from 'tiny-invariant';
import { QuoteSubjectivityPostRequestBody, SubjectivityUpdateRequestBody } from '@lio/api-types';

const otherSubjectivityTypeId = 'clr7x917b0000pev59g3s5uxl';

type SubjectivityFormProps = {
  selectedTab: SubjectivitiesTab.EditSubjectivity | SubjectivitiesTab.NewSubjectivity;
  selectedItem?: SubjectivityItem;
  changeTab: (newTab: SubjectivitiesTab) => void;
  isBroker?: boolean;
  defaultAddMode: 'multiple' | 'single';
  isDialogMode?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  onSubmitDisabledChange?: (isSubmitDisabled: boolean) => void;
  quoteId?: string;
};

export type SubjectivityFormRef = {
  submit: () => void;
  isSubmitDisabled: boolean;
};

type SubjectivityFormData = QuoteSubjectivityPostRequestBody & {
  saveToLibrary: boolean;
};

const submitButtonText: Record<SubjectivitiesTab.EditSubjectivity | SubjectivitiesTab.NewSubjectivity, string> = {
  [SubjectivitiesTab.EditSubjectivity]: 'Save',
  [SubjectivitiesTab.NewSubjectivity]: 'Create',
};

export const SubjectivityFormContent = forwardRef<
  SubjectivityFormRef,
  Omit<SubjectivityFormProps, 'changeTab'> & {
    onClose?: () => void;
    onSuccess?: () => void;
  }
>(
  (
    {
      selectedTab,
      selectedItem,
      isBroker = false,
      defaultAddMode,
      isDialogMode = false,
      onClose,
      onSuccess,
      onSubmitDisabledChange,
      quoteId: propQuoteId,
    },
    ref,
  ) => {
    SubjectivityFormContent.displayName = 'SubjectivityFormContent';
    const paramsQuoteId = useParams<{ quoteId: string }>().quoteId;
    const quoteId = propQuoteId || paramsQuoteId;
    invariant(quoteId);
    const [{ isInternalUser }] = useStore((state) => [state.userAuth], shallow);
    const [isLoadedFromLibrary, setLoadedFromLibrary] = useState(false);
    const [formMode, setFormMode] = useState<'single' | 'multiple'>(defaultAddMode);
    const { structuredQuote } = useQuoteStore((state) => ({ structuredQuote: state.structuredQuote }), shallow);
    const [bulkAddItems, setBulkAddItems] = useState<TSubjectivityLibraryItem[]>([]);
    const { createManualSubjectivity, isPending: isCreateSubjectivityLoading } = useCreateSubjectivity(quoteId ?? '');
    const { updateSubjectivity, isPending: isUpdateSubjectivityLoading } = useUpdateSubjectivity(quoteId ?? '');
    const { quote } = useQuote(quoteId ?? '');
    const { createSubjectivityLibraryItem } = useCreateSubjectivityLibraryItem();

    const { items } = useSearchPaginatedSubjectivityLibrary({
      params: { pageLimit: 1 },
      enabled: !!quoteId,
    });

    const isQuoteTerminal = !!quote?.status.name && quoteStatusTerminal.includes(quote?.status.name);

    const availableLOIS = useMemo(() => {
      if (!structuredQuote) {
        return [];
      }
      const policyQuoteItem = structuredQuote.quoteItems.find((qi) => qi.quoteItemType.codeName === 'Policy');

      return Object.entries(insuranceLineIds).flatMap(([codeName, insuranceLineId]) => {
        const isLOISelected =
          policyQuoteItem?.attributeTypes[codeName as keyof typeof policyQuoteItem.attributeTypes]?.attribute?.value;

        return isLOISelected
          ? [
              {
                id: insuranceLineId,
                label:
                  policyQuoteItem?.attributeTypes[codeName as keyof typeof policyQuoteItem.attributeTypes]?.label ??
                  codeName,
              },
            ]
          : [];
      }) as {
        label: string;
        id: string;
      }[];
    }, [structuredQuote]);

    const availableLOIIds = availableLOIS.map((item) => item.id);

    const handleLibrarySelection = (value: TSubjectivityLibraryItem | string | TSubjectivityLibraryItem[]) => {
      if (Array.isArray(value)) {
        return;
      }
      if (typeof value === 'string') {
        setLoadedFromLibrary(false);
        methods.setValue('label', value);
        methods.trigger('label');
        return;
      }
      setLoadedFromLibrary(true);
      methods.setValue('label', value.name);
      methods.setValue('description', value.description ?? '');
      if (!isBroker) {
        methods.setValue('dueDaysAfterPolicyEffective', value.dueDaysAfterPolicyEffective ?? undefined);
      }
      methods.trigger(['label', 'description', 'dueDaysAfterPolicyEffective']); // Trigger validation for all affected fields
    };

    const formFields: { [fieldName: string]: TFormField } = useMemo(() => {
      const fields = getSubjectivitiesFormFields(
        isBroker,
        isInternalUser,
        selectedItem,
        availableLOIS,
        isLoadedFromLibrary,
      );

      // Only show insurance line and due days fields for non-brokers
      if (isBroker) {
        const {
          insuranceLineId: _insuranceLineId,
          dueDaysAfterPolicyEffective: _dueDaysAfterPolicyEffective,
          ...brokerFields
        } = fields;
        return brokerFields;
      }

      return fields;
    }, [isBroker, selectedItem, isInternalUser, isLoadedFromLibrary, availableLOIS]);

    const defaultValues: SubjectivityFormData = useMemo(
      () => ({
        label: selectedItem?.label ?? '',
        dueDaysAfterPolicyEffective: selectedItem?.dueDaysAfterPolicyEffective ?? 10,
        description: selectedItem?.description ?? '',
        insuranceLineId: selectedItem?.insuranceLine ? selectedItem?.insuranceLine.id : 'Common',
        saveToLibrary: false,
      }),
      [selectedItem],
    );

    const methods = useForm<SubjectivityFormData>({
      mode: 'onChange',
      defaultValues,
    });

    const { watch, formState } = methods;
    const watchedValues = watch();

    const hasFormChanged = useMemo(() => {
      if (selectedTab === SubjectivitiesTab.NewSubjectivity) return true;

      return watchedValues.label !== selectedItem?.label || Object.keys(formState.dirtyFields).length > 0;
    }, [watchedValues, selectedItem, selectedTab, formState.dirtyFields]);

    const isSubmitButtonDisabled: Record<
      SubjectivitiesTab.EditSubjectivity | SubjectivitiesTab.NewSubjectivity,
      boolean
    > = useMemo(
      () => ({
        [SubjectivitiesTab.EditSubjectivity]: isQuoteTerminal || !formState.isValid || !hasFormChanged,
        [SubjectivitiesTab.NewSubjectivity]:
          formMode === 'single'
            ? isQuoteTerminal || !formState.isValid || !hasFormChanged || !watchedValues.label?.trim()
            : bulkAddItems?.length === 0,
      }),
      [isQuoteTerminal, formState.isValid, hasFormChanged, formMode, bulkAddItems?.length, watchedValues.label],
    );

    // Notify parent component when submit disabled state changes
    useEffect(() => {
      onSubmitDisabledChange?.(isSubmitButtonDisabled[selectedTab]);
    }, [isSubmitButtonDisabled, selectedTab, onSubmitDisabledChange]);

    const handleSubmit: Record<
      SubjectivitiesTab.EditSubjectivity | SubjectivitiesTab.NewSubjectivity,
      (data: SubjectivityFormData) => Promise<void>
    > = useMemo(
      () => ({
        [SubjectivitiesTab.EditSubjectivity]: async (data: SubjectivityFormData) => {
          const updatedSubjectivityBody: SubjectivityUpdateRequestBody & { subjectivityId: string } = {
            label: data.label ?? '',
            dueDaysAfterPolicyEffective: isBroker ? undefined : data.dueDaysAfterPolicyEffective,
            status: selectedItem?.status,
            description: data.description === '' ? undefined : data.description,
            subjectivityId: selectedItem?.id as string,
            insuranceLineId:
              data.insuranceLineId && data.insuranceLineId !== '' && data.insuranceLineId !== 'Common'
                ? data.insuranceLineId
                : null,
          };

          await updateSubjectivity(updatedSubjectivityBody);
          onSuccess?.();
          onClose?.();
        },
        [SubjectivitiesTab.NewSubjectivity]: async (data: SubjectivityFormData) => {
          if (formMode === 'multiple') {
            await Promise.all(
              bulkAddItems.map(async (item) => {
                const newSubjectivityBody: QuoteSubjectivityPostRequestBody = {
                  label: item.name,
                  description: item.description ?? undefined,
                  dueDaysAfterPolicyEffective: isBroker ? undefined : (data.dueDaysAfterPolicyEffective ?? 10),
                  subjectivityTypeId: otherSubjectivityTypeId,
                  insuranceLineId:
                    data.insuranceLineId && data.insuranceLineId !== '' && data.insuranceLineId !== 'Common'
                      ? data.insuranceLineId
                      : undefined,
                };

                await createManualSubjectivity(newSubjectivityBody);
              }),
            );

            enqueueSnackbar(`Added ${bulkAddItems.length} subjectivities to the quote`, { variant: ToastType.Success });
          } else {
            // single add
            const newSubjectivityBody: QuoteSubjectivityPostRequestBody = {
              label: data.label ?? '',
              dueDaysAfterPolicyEffective: isBroker ? undefined : data.dueDaysAfterPolicyEffective,
              description: data.description === '' ? undefined : data.description,
              subjectivityTypeId: otherSubjectivityTypeId,
              insuranceLineId:
                data.insuranceLineId && data.insuranceLineId !== '' && data.insuranceLineId !== 'Common'
                  ? data.insuranceLineId
                  : undefined,
            };

            if (data.saveToLibrary) {
              await createSubjectivityLibraryItem({
                name: data.label ?? '',
                description: data.description === '' ? undefined : data.description,
                dueDaysAfterPolicyEffective: isBroker ? undefined : (data.dueDaysAfterPolicyEffective ?? undefined),
                brokerageId: isBroker ? quote?.brokerage.id : undefined,
                insuranceLineId:
                  data.insuranceLineId && data.insuranceLineId !== '' && data.insuranceLineId !== 'Common'
                    ? data.insuranceLineId
                    : undefined,
              });
            }

            await createManualSubjectivity(newSubjectivityBody);
            enqueueSnackbar(`Added subjectivity to the quote`, { variant: ToastType.Success });
          }
          onSuccess?.();
          onClose?.();
        },
      }),
      [
        isBroker,
        selectedItem,
        onSuccess,
        onClose,
        formMode,
        bulkAddItems,
        createManualSubjectivity,
        createSubjectivityLibraryItem,
        quote?.brokerage?.id,
        updateSubjectivity,
      ],
    );

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (formMode === 'single') {
            methods.handleSubmit(handleSubmit[selectedTab])();
          } else {
            handleSubmit[selectedTab]({} as SubjectivityFormData);
          }
        },
        isSubmitDisabled: isSubmitButtonDisabled[selectedTab],
        onSuccess,
      }),
      [formMode, selectedTab, methods, handleSubmit, isSubmitButtonDisabled, onSuccess],
    );

    return (
      <>
        {!isDialogMode && (
          <Grid
            container
            sx={{
              flexDirection: 'column',
              flexGrow: 3,
              gap: 2,
              justifyContent: 'flex-start',
              padding: 2,
            }}
          >
            {!selectedItem && items && items.length > 0 && (
              <Grid>
                <Button
                  variant='text'
                  size='small'
                  sx={{ padding: 0, fontSize: 12 }}
                  onClick={() => {
                    setFormMode(formMode === 'single' ? 'multiple' : 'single');
                  }}
                >
                  {formMode === 'single' ? 'Add Subjectivities from Library' : 'Add Manual Subjectivity'}
                </Button>
              </Grid>
            )}

            {formMode === 'multiple' ? (
              <SubjectivityBulkAddForm
                availableLOIIds={availableLOIIds}
                selectedItems={bulkAddItems}
                onChangeSelectedItems={setBulkAddItems}
              />
            ) : (
              <Form formId='subjectivity-form' methods={methods}>
                <Grid>
                  <SubjectivityLibraryTypeAhead
                    availableLOIIds={availableLOIIds}
                    defaultValue={selectedItem?.label}
                    onSelectFromList={handleLibrarySelection}
                    onChangeInput={(newValue: string) => {
                      methods.setValue('label', newValue);
                      methods.trigger('label');
                    }}
                  />
                </Grid>

                <FormSection fields={formFields} />
              </Form>
            )}
          </Grid>
        )}

        {isDialogMode && (
          <Box sx={{ p: 3 }}>
            {!selectedItem && items && items.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant='text'
                  size='small'
                  sx={{ padding: 0, fontSize: 12 }}
                  onClick={() => {
                    setFormMode(formMode === 'single' ? 'multiple' : 'single');
                  }}
                >
                  {formMode === 'single' ? 'Add Subjectivities from Library' : 'Add Manual Subjectivity'}
                </Button>
              </Box>
            )}

            {formMode === 'multiple' ? (
              <SubjectivityBulkAddForm
                availableLOIIds={availableLOIIds}
                selectedItems={bulkAddItems}
                onChangeSelectedItems={setBulkAddItems}
              />
            ) : (
              <Form formId='subjectivity-form-dialog' methods={methods}>
                <Box sx={{ mb: 2 }}>
                  <SubjectivityLibraryTypeAhead
                    availableLOIIds={availableLOIIds}
                    defaultValue={selectedItem?.label}
                    onSelectFromList={handleLibrarySelection}
                    onChangeInput={(newValue: string) => {
                      methods.setValue('label', newValue);
                      methods.trigger('label');
                    }}
                  />
                </Box>

                <FormSection fields={formFields} />
              </Form>
            )}
          </Box>
        )}

        {!isDialogMode && (
          <Grid
            container
            sx={{
              flexDirection: 'column-reverse',
              gap: 2,
              justifyContent: 'flex-end',
              paddingY: 2,
              borderTop: '1px solid rgba(0, 0, 0, 0.1)', // TODO theme
            }}
          >
            <Button
              fullWidth
              variant='contained'
              disabled={isSubmitButtonDisabled[selectedTab]}
              onClick={() => {
                if (formMode === 'single') {
                  methods.handleSubmit(handleSubmit[selectedTab])();
                } else {
                  handleSubmit[selectedTab]({} as SubjectivityFormData);
                }
              }}
              isLoading={isCreateSubjectivityLoading || isUpdateSubjectivityLoading}
              sx={{ margin: 'auto' }}
            >
              {selectedTab === SubjectivitiesTab.NewSubjectivity && formMode === 'multiple'
                ? 'Add Selected Subjectivities'
                : submitButtonText[selectedTab]}
            </Button>
          </Grid>
        )}
      </>
    );
  },
);

export const SubjectivityForm = ({
  selectedTab,
  selectedItem,
  changeTab: _changeTab,
  isBroker,
  defaultAddMode,
  isDialogMode = false,
  onClose,
  onSuccess,
}: SubjectivityFormProps) => {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!isDialogMode && <SidebarHeader title='Details' />}
      <SubjectivityFormContent
        selectedTab={selectedTab}
        selectedItem={selectedItem}
        isBroker={isBroker}
        defaultAddMode={defaultAddMode}
        isDialogMode={isDialogMode}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Box>
  );
};
